import { useCallback, useEffect, useRef } from 'react'
import * as logger from 'loglevel'
import { propagateParticipantExit, propagatePresenceEnter } from './childAppsDom'

export function useFrameChannels({
  anearEvent,
  anearParticipant,
  userAuth,
  bootParticipantHandler,
  displayMessage,
  activeChildAppsRef,
}) {
  const participantSubscribedRef = useRef(false)
  // Participant instance that currently owns EventFrame Ably subscriptions (private/participants/force_shutdown)
  const subscribedParticipantRef = useRef(null)
  const spectatorSubscribedRef = useRef(false)
  const prevParticipantRef = useRef(anearParticipant)

  const displayPrivateMsg = useCallback(
    msg => {
      // Keep this quiet; message attribution is handled in UserMessaging logs.
      displayMessage(msg)
    }, [displayMessage]
  )
  const displayParticipantsMsg = useCallback(
    msg => {
      logger.info(`participants message: ${msg.name}`);
      displayMessage(msg)
    }, [displayMessage]
  )
  const displaySpectatorsMsg = useCallback(
    msg => {
      logger.info(`spectators message: ${msg.name}`);
      displayMessage(msg)
    }, [displayMessage]
  )

  const forceShutdownHandler = useCallback(message => {
    logger.info("Received force shutdown message:", message)
    const reason = message.data.content.reason
    bootParticipantHandler(reason)
  }, [bootParticipantHandler])

  const setupParticipant = useCallback(async () => {
    if (!anearParticipant || participantSubscribedRef.current) return

    participantSubscribedRef.current = true

    // PARTICIPANTS_DISPLAY is for participants only. In a hosted event the HOST is not a participant
    // (they have their own meta.host views and anchors); skip participants channel. In non-hosted
    // events (e.g. Spades) the creator is a participant and receives the same public view.
    if (!anearEvent.hosted || !anearParticipant.isHost()) {
      anearParticipant.subscribeEventParticipantsMessages(displayParticipantsMsg)
    }

    anearParticipant.subscribeEventPrivateMessages(displayPrivateMsg)
    anearParticipant.subscribeForceShutdownMessages(forceShutdownHandler)
    subscribedParticipantRef.current = anearParticipant

    await anearEvent.closeSpectatorsChannel()
    spectatorSubscribedRef.current = false

    // Enter presence on actions channel - this notifies the AEM that participant is ready
    // NOTE: Child participants will enter presence when they're created in ChildEventFrame.setupParticipant
    // We don't propagate here because child events might not be initialized yet at this point
    const requiresGeoLocation = anearEvent.requiresGeoLocationToParticipate()
    await anearParticipant.setActionPresence(requiresGeoLocation)

    // For reconnect scenarios: If child events are already initialized, propagate presence enter
    // This handles the case where parent participant reconnects after child events are already set up
    await propagatePresenceEnter(activeChildAppsRef.current, requiresGeoLocation)
  }, [anearParticipant, anearEvent, displayPrivateMsg, displayParticipantsMsg, forceShutdownHandler, activeChildAppsRef])

  const setupSpectator = useCallback(async () => {
    if (anearEvent.spectatorsAllowed() && !spectatorSubscribedRef.current) {

      spectatorSubscribedRef.current = true

      await anearEvent.subscribeSpectatorDisplayMessages(
        userAuth.anearUser(),
        displaySpectatorsMsg
      )
    }
  }, [anearEvent, userAuth, displaySpectatorsMsg])

  // Run on mount or anearParticipant change
  useEffect(() => {
    let cancelled = false
    const prevParticipant = prevParticipantRef.current
    prevParticipantRef.current = anearParticipant

    const clearParticipantFrameSubs = (p) => {
      if (!p) return
      p.unSubscribeEventPrivateMessages()
      p.unSubscribeEventParticipantsMessages()
      p.unSubscribeForceShutdownMessages()
    }

    const setup = async () => {
      // Handle parent participant exit: propagate exit to child events
      // This ensures child participant state mirrors parent participant state
      // Since there's no back-channel between parent/child AppM/AEM, ABR must synchronize
      if (prevParticipant && !anearParticipant) {
        // Parent participant exited - propagate exit to all child events
        // Use PARTICIPANT_EXIT to match parent's voluntary exit state
        await propagateParticipantExit(activeChildAppsRef.current)
      }

      // Drop prior frame subscriptions before re-setup (participant swap, handler refresh, strict mode)
      if (participantSubscribedRef.current) {
        clearParticipantFrameSubs(subscribedParticipantRef.current)
        subscribedParticipantRef.current = null
        participantSubscribedRef.current = false
      }
      if (spectatorSubscribedRef.current) {
        spectatorSubscribedRef.current = false
      }

      if (cancelled) return

      if (anearParticipant) {
        // Parent participant is present (initial join or reconnect)
        await setupParticipant()
        // Note: setupParticipant already propagates presence enter to child events
        // For reconnect scenarios, this ensures child participants also reconnect
      } else {
        await setupSpectator()
      }
    }

    setup()

    return () => {
      cancelled = true
      if (participantSubscribedRef.current) {
        clearParticipantFrameSubs(subscribedParticipantRef.current)
        subscribedParticipantRef.current = null
        participantSubscribedRef.current = false
      }
      if (spectatorSubscribedRef.current) {
        spectatorSubscribedRef.current = false
      }
    }
  }, [anearParticipant, setupParticipant, setupSpectator, activeChildAppsRef])
}
